import { useEffect } from 'react';
import { useUIState } from '../contexts/UIStateContext';

/**
 * A COMBINAÇÃO de tecla não é traduzida: é o que está impresso no teclado.
 * O rótulo e a categoria viram chaves do dicionário (shell.shortcuts), porque
 * este módulo é importado fora do React e não sabe o idioma.
 */
export type ShortcutCategory = 'navigation' | 'editing' | 'selection';

interface ShortcutDef {
  key: string;
  labelKey: keyof typeof import('../i18n/locales/pt/shell').default.shortcuts.list;
  category: ShortcutCategory;
}

export const SHORTCUT_MAP: ShortcutDef[] = [
  // Navigation
  { key: 'Ctrl+K', labelKey: 'globalSearch', category: 'navigation' },
  { key: 'Ctrl+1', labelKey: 'goToGantt', category: 'navigation' },
  { key: 'Ctrl+2', labelKey: 'goToKanban', category: 'navigation' },
  { key: 'Ctrl+3', labelKey: 'goToCalendar', category: 'navigation' },
  { key: 'Ctrl+4', labelKey: 'goToTeam', category: 'navigation' },
  { key: 'Ctrl+0', labelKey: 'goToHome', category: 'navigation' },

  // Task editing
  { key: 'Ctrl+Z', labelKey: 'undo', category: 'editing' },
  { key: 'Ctrl+Y', labelKey: 'redo', category: 'editing' },
  { key: 'Tab', labelKey: 'indentTask', category: 'editing' },
  { key: 'Shift+Tab', labelKey: 'outdentTask', category: 'editing' },
  { key: 'Alt+Up', labelKey: 'moveTaskUp', category: 'editing' },
  { key: 'Alt+Down', labelKey: 'moveTaskDown', category: 'editing' },
  { key: 'Ctrl+D', labelKey: 'duplicateTask', category: 'editing' },
  { key: 'Ctrl+V', labelKey: 'pasteTasks', category: 'editing' },
  { key: 'Delete', labelKey: 'deleteTask', category: 'editing' },

  // Selection
  { key: 'Ctrl+Click', labelKey: 'multiSelect', category: 'selection' },
  { key: 'Shift+Click', labelKey: 'rangeSelect', category: 'selection' },
  { key: 'Esc', labelKey: 'clearSelect', category: 'selection' },
];

const MODULE_SHORTCUTS: Record<string, string> = {
  '1': 'gantt',
  '2': 'kanban',
  '3': 'calendar',
  '4': 'team',
  '0': 'home',
};

export function useGlobalKeyboardShortcuts(callbacks?: {
  onOpenSearch?: () => void;
  onOpenShortcuts?: () => void;
}) {
  const { changeModule } = useUIState();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isCtrl = e.ctrlKey || e.metaKey;

      // Ctrl+K: Open search
      if (isCtrl && e.key === 'k') {
        e.preventDefault();
        callbacks?.onOpenSearch?.();
        return;
      }

      // ?: Open keyboard shortcuts panel
      if (e.key === '?' && !isCtrl && !e.altKey) {
        e.preventDefault();
        callbacks?.onOpenShortcuts?.();
        return;
      }

      // Ctrl+0-4: Module navigation
      if (isCtrl && MODULE_SHORTCUTS[e.key]) {
        e.preventDefault();
        changeModule(MODULE_SHORTCUTS[e.key]);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [changeModule, callbacks]);
}
